import clsx from 'clsx';
import { type FC, type ReactNode, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDebouncedValue, useOutsideClickRef } from 'rooks';
import type { IOrganizationRoleEnum } from 'src/graphql/types';
import useSearchPlayers from 'src/hooks/players/useSearchPlayers';
import DropdownPanel from '../0100_dropdown_panel';
import Input from '../0100_input';
import SearchResult from '../0300_search_result';

interface IProps {
  logistics?: boolean;
  isDisabled?: boolean;
  title?: string;
  placeholder: string;
  playersOnly?: boolean;
  excludePlayerIds?: number[];
  withCharacters?: boolean;
  accessLevelFilters?: IOrganizationRoleEnum[];
  branchId?: number;
  eventId?: number;
  modId?: number;
  characterIcon?: ({ characterId }: { characterId?: number }) => ReactNode;
  displayCharacterIf?: ({
    characterId,
    playerId,
  }: {
    characterId: number;
    playerId: number;
  }) => boolean;
  displayPlayerIf?: ({
    characterIds,
    playerId,
  }: {
    characterIds: number[];
    playerId: number;
  }) => boolean;
  onBlur?: () => void;
  onFocus?: () => void;
  onSearchResultClick?: ({
    playerId,
    playerName,
    characterId,
    characterName,
  }: {
    playerId?: number;
    playerName?: string;
    characterId?: number;
    characterName?: string;
  }) => void;
  onQueryChange?: (query: string) => void;
}

const PlayerSearch: FC<IProps> = ({
  logistics,
  isDisabled,
  title,
  accessLevelFilters,
  branchId,
  eventId,
  modId,
  placeholder,
  playersOnly,
  excludePlayerIds = [],
  withCharacters = false,
  characterIcon,
  displayCharacterIf,
  displayPlayerIf,
  onFocus,
  onSearchResultClick,
  onQueryChange,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [ref] = useOutsideClickRef(() => setIsFocused(false));

  const { watch, register } = useForm({
    defaultValues: { query: '' },
  });
  const { query } = watch();
  const [debouncedQuery] = useDebouncedValue(query, 250);

  const { data, fetching } = useSearchPlayers({
    query: debouncedQuery,
    playersOnly,
    accessLevelFilters,
    branchId,
    eventId,
    modId,
    logistics,
  });

  const { ...rest } = register('query');
  const executableQuery = watch('query').trim().length >= 1;
  const filteredData = useMemo(
    () =>
      data?.searchPlayer
        .filter(
          x =>
            !excludePlayerIds.includes(
              x.__typename === 'User'
                ? x.id
                : x.__typename === 'Character'
                  ? x.user.id
                  : 0,
            ),
        )
        .filter(x =>
          x.__typename === 'Character'
            ? (displayCharacterIf?.({
                characterId: x.id,
                playerId: x.user.id,
              }) ?? true)
            : x.__typename === 'User'
              ? (displayPlayerIf?.({
                  characterIds: x.characters.map(x => x.id),
                  playerId: x.id,
                }) ?? true)
              : true,
        ) ?? [],
    [data, displayCharacterIf, displayPlayerIf, excludePlayerIds],
  );

  return (
    <div className="grid gap-1">
      {title && (
        <div className="uppercase text-xs text-juno-gray-200">{title}</div>
      )}
      <div ref={ref} className="flex">
        <Input
          fullWidth
          disabled={isDisabled}
          placeholder={placeholder}
          onFocus={() => {
            setIsFocused(true);
            onFocus?.();
          }}
          {...register('query')}
          onChange={x => {
            rest.onChange(x);
            onQueryChange?.(x.target.value);
          }}
        />
      </div>
      <DropdownPanel
        isExpanded={isFocused}
        zIndex={10}
        className={clsx(
          'mt-2 border-juno-gray-700',
          isFocused && executableQuery && 'border',
        )}
      >
        {!fetching &&
          executableQuery &&
          filteredData.map(x => {
            if (x.__typename === 'User') {
              if (withCharacters) {
                return (
                  <div key={x.id}>
                    <button
                      type="button"
                      disabled
                      className="px-4 pt-2 flex justify-between border-l-4 border-transparent"
                      onClick={e => e.preventDefault()}
                    >
                      <div className="w-12 pr-1 text-right">{x.id}</div>
                      <div className="w-48 text-left  text-juno-gray-200">
                        {x.fullName}
                        {x.preferredName && (
                          <div>
                            a.k.a. {[x.firstName, x.lastName].join(' ')}
                          </div>
                        )}
                      </div>
                    </button>
                    <div className="pr-1">
                      {x.characters.map(character => (
                        <SearchResult
                          characterOnly
                          key={`user-${x.id}-character-${character.id}`}
                          playerId={x.id}
                          playerName={x.fullName}
                          characterName={character.name}
                          characterId={character.id}
                          characterIcon={characterIcon}
                          onClick={onSearchResultClick}
                        />
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <SearchResult
                  key={`user-${x.id}`}
                  playerName={x.fullName}
                  playerFirstName={x.firstName}
                  playerLastName={x.lastName}
                  playerHasPreferredName={!!x.preferredName}
                  playerId={x.id}
                  userOrganization={x.userOrganization}
                  onClick={onSearchResultClick}
                />
              );
            }

            // eslint-disable-next-line no-underscore-dangle
            if (x.__typename === 'Character') {
              return (
                <SearchResult
                  key={`character-${x.id}`}
                  characterName={x.name}
                  characterId={x.id}
                  characterIcon={characterIcon}
                  playerName={x.user.fullName}
                  playerFirstName={x.user.firstName}
                  playerLastName={x.user.lastName}
                  playerHasPreferredName={!!x.user.preferredName}
                  playerId={x.user.id}
                  userOrganization={x.user.userOrganization}
                  onClick={onSearchResultClick}
                />
              );
            }

            return null;
          })}

        {!fetching && executableQuery && filteredData.length === 0 && (
          <div className="p-2 w-56">No Results :(</div>
        )}
        {fetching && executableQuery && (
          <div className="p-2 w-56">Searching...</div>
        )}
      </DropdownPanel>
    </div>
  );
};

export default PlayerSearch;
